import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const VerificationSuccess = ({ userEmail, onResendVerification, onVerificationComplete }) => {
  const navigate = useNavigate();
  const { user, signUp } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer effect
  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Monitor auth state for email confirmation
  useEffect(() => {
    if (user && user?.email_confirmed_at) {
      // User has been confirmed, proceed to profile
      console.log('Email verification detected, completing registration');
      if (onVerificationComplete) {
        onVerificationComplete();
      } else {
        navigate('/user-profile', { replace: true });
      }
    }
  }, [user, navigate, onVerificationComplete]);

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    setResendMessage('');

    try {
      // Get stored registration data
      const registrationData = JSON.parse(localStorage?.getItem('registrationData') || '{}');
      
      if (registrationData?.email) {
        // Trigger a new verification email by attempting signup with same email
        const { error } = await signUp(registrationData?.email, 'temp-password-for-resend', {
          resend: true // Flag to indicate this is a resend attempt
        });

        if (error && !error?.includes('already registered')) {
          // If error is not about existing user, show it
          setResendMessage('Failed to resend verification email. Please try again.');
        } else {
          // Success or user already exists (expected for resend)
          setResendMessage('Verification email sent! Please check your inbox and spam folder.');
          setResendCooldown(60); // 60 second cooldown
        }
      } else if (onResendVerification) {
        await onResendVerification();
        setResendMessage('Verification email sent! Please check your inbox.');
        setResendCooldown(60);
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setResendMessage('Failed to resend email. Please try again in a moment.');
    } finally {
      setIsResending(false);
    }

    // Clear message after 8 seconds
    setTimeout(() => {
      setResendMessage('');
    }, 8000);
  };

  const handleBackToLogin = () => {
    localStorage?.removeItem('registrationData');
    navigate('/login');
  };

  const handleBackToSignup = () => {
    localStorage?.removeItem('registrationData');
    // Refresh the page to reset the registration form
    window?.location?.reload();
  };

  return (
    <div className="text-center">
      <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <Icon name="Mail" size={32} color="var(--color-primary)" />
      </div>
      
      <h2 className="text-2xl font-heading font-semibold text-foreground mb-4">
        Check Your Email
      </h2>
      
      <div className="space-y-4 text-muted-foreground font-body mb-6">
        <p>
          We've sent a verification link to:
        </p>
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-foreground font-medium text-lg">
            {userEmail}
          </p>
        </div>
        <p>
          Click the link in the email to verify your account and complete your registration.
        </p>
      </div>

      {/* Important Information */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5 text-primary" />
          <div className="text-left text-sm">
            <p className="font-medium text-foreground mb-2">Can't find the email?</p>
            <ul className="space-y-1 text-muted-foreground text-xs">
              <li>• Check your spam/junk folder</li>
              <li>• Make sure you entered the correct email address</li>
              <li>• The email may take a few minutes to arrive</li>
              <li>• Look for an email from "Holistic Directories"</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Resend Message */}
      {resendMessage && (
        <div className={`p-4 rounded-lg mb-6 ${
          resendMessage?.includes('sent') || resendMessage?.includes('Success')
            ? 'bg-success/10 border border-success/20 text-success' :'bg-destructive/10 border border-destructive/20 text-destructive'
        }`}>
          <div className="flex items-center justify-center space-x-2">
            <Icon 
              name={resendMessage?.includes('sent') ? "CheckCircle" : "AlertCircle"} 
              size={16} 
            />
            <p className="text-sm font-body">{resendMessage}</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-4">
        <Button
          onClick={handleResend}
          variant="outline"
          fullWidth
          disabled={isResending || resendCooldown > 0}
          iconName={isResending ? "Loader2" : "RefreshCw"}
          iconPosition="left"
          className={isResending ? "animate-spin" : ""}
        >
          {isResending 
            ? 'Resending...' 
            : resendCooldown > 0 
              ? `Resend in ${resendCooldown}s` 
              : 'Resend Verification Email'
          }
        </Button>

        <div className="flex space-x-3">
          <Button
            onClick={handleBackToSignup}
            variant="ghost"
            className="flex-1"
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Back to Sign Up
          </Button>

          <Button
            onClick={handleBackToLogin}
            variant="ghost"
            className="flex-1"
            iconName="LogIn"
            iconPosition="left"
          >
            Go to Login
          </Button>
        </div>
      </div>

      {/* Contact Support */}
      <div className="mt-8 pt-6 border-t border-border">
        <p className="text-sm text-muted-foreground font-body">
          Still having trouble? Contact our support team at{' '}
          <a 
            href="mailto:support@holisticdirectories.co.uk" 
            className="text-primary hover:text-primary/80 underline font-medium"
          >
            support@holisticdirectories.co.uk
          </a>
        </p>
      </div>
    </div>
  );
};

export default VerificationSuccess;